import React from "react";
import { Table, Tag, Tooltip, Progress, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import ApplicationPagination from "../../applications/components/ApplicationPagination";


const CandidateAvatar: React.FC<{ name: string; url?: string }> = ({ name, url }) => {
  const [error, setError] = React.useState(false);

  if (url && !error) {
    return (
      <img
        src={url}
        alt={name}
        className="rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, objectFit: "cover" }}
        onError={() => setError(true)}
      />
    );
  }

  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {(name || "?")
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()}
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  applied: "blue",
  shortlisted: "purple",
  screening: "gold",
  interview: "cyan",
  offer: "green",
  hired: "success",
  rejected: "red",
  withdrawn: "default",
};

interface CandidateTableProps {
  candidates: any[];
  page: number;
  total: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}

const CandidateTable: React.FC<CandidateTableProps> = ({
  candidates,
  page,
  total,
  loading,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const columns: ColumnsType<any> = [
    {
      title: "Sr.",
      key: "srNo",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {(page - 1) * 20 + index + 1}
        </span>
      ),
    },
    {
      title: "Candidate",
      key: "candidate",
      width: 250,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CandidateAvatar name={r.full_name} url={r.profile_pic_serve_url} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13.5,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 180,
              }}
            >
              {r.full_name}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Role / Company",
      key: "role",
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
            {r.current_designation || <span className="text-muted">—</span>}
          </div>
          {r.current_company && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              <i className="ti ti-building" style={{ marginRight: 3 }} />
              {r.current_company}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Experience",
      dataIndex: "total_experience_years",
      key: "exp",
      width: 100,
      align: "center",
      render: (yrs) =>
        yrs != null ? (
          <Tag color="geekblue" style={{ fontSize: 12 }}>
            {yrs} yr{yrs !== 1 ? "s" : ""}
          </Tag>
        ) : (
          <span className="text-muted fs-12">—</span>
        ),
    },
    {
      title: "Applications",
      dataIndex: "total_applications",
      key: "apps",
      width: 110,
      align: "center",
      sorter: (a, b) => a.total_applications - b.total_applications,
      render: (count) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "#ede9fe",
            color: "#7c3aed",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {count}
        </span>
      ),
    },
    {
      title: "Max ATS",
      dataIndex: "max_ats_score",
      key: "ats",
      width: 110,
      align: "center",
      sorter: (a, b) =>
        parseFloat(a.max_ats_score || "0") - parseFloat(b.max_ats_score || "0"),
      render: (score) => {
        const num = score ? parseFloat(score) : null;
        if (num === null) return <span className="text-muted fs-12">N/A</span>;
        return (
          <Tooltip title={`${num.toFixed(0)}% ATS Match`}>
            <Progress
              type="circle"
              percent={Math.round(num)}
              size={36}
              strokeColor={
                num >= 80 ? "#22c55e" : num >= 60 ? "#f59e0b" : "#ef4444"
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Latest Status",
      dataIndex: "latest_status",
      key: "status",
      width: 130,
      render: (status) =>
        status ? (
          <Tag
            color={STATUS_COLORS[status] || "default"}
            style={{ fontSize: 11.5, textTransform: "capitalize" }}
          >
            {status.replace(/_/g, " ")}
          </Tag>
        ) : (
          <span className="text-muted fs-12">—</span>
        ),
    },
    {
      title: "Last Applied",
      dataIndex: "last_applied_at",
      key: "last_applied",
      width: 120,
      sorter: (a, b) =>
        new Date(a.last_applied_at || 0).getTime() -
        new Date(b.last_applied_at || 0).getTime(),
      render: (date) =>
        date ? (
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            <i className="ti ti-calendar me-1" />
            {humanReadableDate(date)}
          </span>
        ) : (
          <span className="text-muted fs-12">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, r) => (
        <Button
          size="small"
          type="default"
          style={{ borderRadius: 6 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              all_routes.recruitmentCandidateView.replace(
                ":id",
                String(r.candidate_id),
              ),
            );
          }}
        >
          <i className="ti ti-eye" />
        </Button>
      ),
    },
  ];

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body p-0">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center bg-white"
            style={{ minHeight: 400, borderRadius: 12 }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-3 text-muted">Loading candidates...</p>
            </div>
          </div>
        ) : candidates.length > 0 ? (
          <div className="bg-white" style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <Table
              className="custom-data-table"
              columns={columns}
              dataSource={candidates}
              rowKey="candidate_id"
              size="middle"
              pagination={false}
              scroll={{ x: 1200 }}
              rowClassName={(_, index) =>
                index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
              }
              onRow={(r) => ({
                onClick: () =>
                  navigate(
                    all_routes.recruitmentCandidateView.replace(
                      ":id",
                      String(r.candidate_id),
                    ),
                  ),
                style: { cursor: "pointer" },
              })}
            />
            <ApplicationPagination
              currentPage={page}
              totalPages={Math.ceil(total / 20)}
              total={total}
              limit={20}
              hasNextPage={page * 20 < total}
              hasPrevPage={page > 1}
              onPageChange={onPageChange}
              onLimitChange={() => { }}
            />
          </div>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center bg-white"
            style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
          >
            <div className="text-center">
              <i className="ti ti-users text-muted" style={{ fontSize: "4rem" }} />
              <h5 className="mt-3 mb-2 fw-semibold">No Candidates Found</h5>
              <p className="text-muted mb-0">Try adjusting your search or check back later.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateTable;
